// Zod contract for the owner's monthly marketing report (`{salon}.hayven.salon/report/{token}` and the staff preview):
// one payload, built by the API's marketing month report builder; the landing page and the web app parse exactly what
// the API sends. The personal data is `recentGuests` (a first name and a last initial, built in the API) and `team` (the
// names and photos of the salon's own marketing people), on a link the salon can reset. Never a phone or an email.
import { z } from 'zod';
import { SOCIAL_REPORT_MONTH_PATTERN, SOCIAL_REPORT_TOKEN_PATTERN } from '../social-report/social-report-schemas';
import { ONLINE_OFFER_PLACEMENTS } from '../../reports/hayven-visit-sources';
import type { OfferPlacement } from '../new-client-offer/new-client-offer-enums';
import type { VoucherSourceGroup } from '../vouchers/voucher-source-groups';
import {
  ownerReportCampaignRowSchema,
  ownerReportDoorsSchema,
  ownerReportGoogleAdsSchema,
  ownerReportGoogleSchema,
  ownerReportLoyaltySchema,
  ownerReportMetaAdsSchema,
  ownerReportRecentGuestsSchema,
  ownerReportSinceStartSchema,
  ownerReportSocialSchema,
  ownerReportTotalsSchema,
  ownerReportTrendPointSchema,
} from './owner-report-result-blocks-schema';
import { addedBlock, count, isoDate, reportMonthSchema } from './owner-report-schema-primitives';
import {
  OWNER_REPORT_HERO_MODES,
  ownerReportReachSchema,
  ownerReportSetupFactsSchema,
  ownerReportTeamMemberSchema,
  ownerReportWorkSchema,
} from './owner-report-team-work-schema';
import { ownerReportTeamInputSchema } from './owner-report-team-input-schema';

/** "YYYY-MM", a salon-local calendar month (same format as the social report). */
export const OWNER_REPORT_MONTH_PATTERN = SOCIAL_REPORT_MONTH_PATTERN;
/** A report link token: 32 random bytes, base64url (43 chars), the same shape as the social report's. */
export const OWNER_REPORT_TOKEN_PATTERN = SOCIAL_REPORT_TOKEN_PATTERN;
/** Longest "Next month" plan the marketing team can write for one month. */
export const OWNER_REPORT_NOTE_MAX_LENGTH = 600;
/** How many salon-calendar months the report offers, the current one included. */
export const OWNER_REPORT_MONTHS_OFFERED = 12;

/**
 * Where a New Client Offer counts as "got online" (ads, Comment → DM, the Bio Page, the salon's own site): the window's
 * placement (the arrival sign-up's form, else the claim's own). Derived from Reports' `ONLINE_OFFER_PLACEMENTS` (a
 * `Record` over every placement, so a new placement must be classified before it compiles) minus GIVEAWAY: a giveaway
 * thank-you pass is a text to the salon's own entrants, so the report counts it under "Texted your clients", not as the
 * ads' work (it would flatter the cost per guest). Counter, kiosk, staff, referral and creator claims are never online.
 */
export const OWNER_REPORT_ONLINE_OFFER_PLACEMENTS: readonly OfferPlacement[] = ONLINE_OFFER_PLACEMENTS.filter(
  (placement) => placement !== 'GIVEAWAY'
);

/**
 * The voucher source groups the report counts as "got online" — the same reach as the offer's online placements: ads +
 * Comment → DM and the Bio Page. "Other links" (text keyword, referral, creator), printed, desk and texted vouchers are
 * not the ads' work.
 */
export const OWNER_REPORT_ONLINE_VOUCHER_GROUPS = ['ADS_DM', 'BIO_PAGE'] as const satisfies readonly VoucherSourceGroup[];

export const ownerReportMonthSchema = reportMonthSchema;
export type OwnerReportMonth = z.infer<typeof ownerReportMonthSchema>;

export const ownerReportMonthOptionSchema = z.object({
  month: ownerReportMonthSchema,
  /** The month is still running ("so far"). */
  partial: z.boolean(),
});
export type OwnerReportMonthOption = z.infer<typeof ownerReportMonthOptionSchema>;

/**
 * What the ads' guests were worth, never $0: real sales of those visits (Hayven checkout), else visits × the average
 * ticket the owner gave ("about"); null when neither is known (the line is hidden).
 */
export const ownerReportValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SALES'), cents: z.number().int().positive() }),
  z.object({
    kind: z.literal('ESTIMATE'),
    cents: z.number().int().positive(),
    visits: z.number().int().positive(),
    averageTicketCents: z.number().int().positive(),
  }),
]);
export type OwnerReportValue = z.infer<typeof ownerReportValueSchema>;

const gotAndCameSchema = z.object({ got: count, cameIn: count });

export const ownerReportSchema = z.object({
  month: ownerReportMonthSchema,
  /** The month's first day and its last day (today while the month runs), salon calendar. */
  from: isoDate,
  to: isoDate,
  /** The month is still running. */
  partial: z.boolean(),
  /** The salon's setup facts the wording reads. */
  setup: z.object({
    visitsProvedBy: z.enum(['SALE', 'CHECK_IN', 'COUNTER']),
    money: z.enum(['SALES', 'MENU_ESTIMATE', 'TURNS_ESTIMATE', 'NONE']),
    /** Picks the footnote at a Hayven checkout salon. Absent on a report from an older API. */
    perkRedeem: z.enum(['CHECK_IN', 'COUNTER_QR']).optional(),
  }),
  /** Came in is proven only at the Hayven stand: every came-in number is the least that came ("At least 45"). */
  cameInAtLeast: z.boolean(),
  /** Null only from an older API. `REACH` when `hero.cameIn` is 0. */
  heroMode: addedBlock(z.enum(OWNER_REPORT_HERO_MODES)),
  /** Null outside `REACH`, and in it when there is nothing to count: the page shows the setup alone. */
  reach: addedBlock(ownerReportReachSchema),
  hero: z.object({
    label: z.string(),
    note: z.string(),
    /**
     * Offers and vouchers got online that came in during the month, used or tapped (= `ads.cameIn`). Counts claims, so it
     * adds up with the offer's and the vouchers' own numbers: a guest who used the offer and a voucher counts twice, even
     * on one visit.
     */
    cameIn: count,
    /** People among those who were new to the salon, each person once. */
    newGuests: count,
    /** The month before, for "12 more than September"; null before the salon's first month with marketing. */
    previous: z.object({ month: ownerReportMonthSchema, cameIn: count }).nullable(),
    /** `cameIn − previous.cameIn`; null without a month before. */
    vsLastMonth: z.number().int().nullable(),
  }),
  ads: z.object({
    /** Meta + Google spend in the month, as the ad accounts report it; null when none is on record. */
    spendCents: z.number().int().positive().nullable(),
    /**
     * New Client Offers and vouchers people got online in the month: the ads' reach only. Its offer part is at most the
     * New Client Offer row of `byCampaign` (every placement) and Reports' "Signed up" (which also counts giveaway thank-you
     * passes and sign-ups through a text keyword, referral or creator link).
     */
    gotOffer: count,
    cameIn: count,
    /** Spend ÷ came in; null without spend or without a guest. */
    costPerGuestCents: z.number().int().positive().nullable(),
    value: ownerReportValueSchema.nullable(),
    /** Each platform's `gotOffer` adds up to `gotOffer` when both blocks are present. */
    meta: addedBlock(ownerReportMetaAdsSchema),
    google: addedBlock(ownerReportGoogleAdsSchema),
  }),
  /** The hero's guests by door; adds up to `hero.cameIn`. */
  doors: addedBlock(ownerReportDoorsSchema),
  /** The month's visits from marketing, with texts and printed vouchers added to the main number. */
  totals: addedBlock(ownerReportTotalsSchema),
  /** Up to 6 months ending at the report's month, oldest first, never before the first marketing month. */
  trend: addedBlock(z.array(ownerReportTrendPointSchema)),
  /** Null until the report's month is at least the salon's second marketing month. */
  sinceStart: addedBlock(ownerReportSinceStartSchema),
  recentGuests: addedBlock(ownerReportRecentGuestsSchema),
  google: addedBlock(ownerReportGoogleSchema),
  /** Null without a comment, a post or a guest in the month; `topPosts` can be empty. */
  social: addedBlock(ownerReportSocialSchema),
  /** The New Client Offer and every voucher / giveaway campaign with activity in the month. */
  byCampaign: z.array(ownerReportCampaignRowSchema),
  withoutAds: z.object({
    /**
     * Gifts texted to the salon's clients — vouchers texted to clients plus giveaway thank-you gifts (vouchers and New
     * Client Offer passes): given in the month → came in during the month.
     */
    texted: gotAndCameSchema,
    /** Printed vouchers saved in the month → came in during the month. */
    printed: gotAndCameSchema,
    clientList: z.object({
      /** Clients with a phone added in the month. */
      newClients: count,
      /** Phone numbers of clients a marketing text can reach today (one per number, as a bulk send texts). */
      total: count,
    }),
    /** Null when no card was used or started in the month. */
    loyalty: addedBlock(ownerReportLoyaltySchema),
  }),
  /** The AM first, then the SMM, 3 at most; null when the salon has neither, and then `preparedBy` names no agency. */
  team: addedBlock(z.array(ownerReportTeamMemberSchema)),
  /** Null when the read failed. */
  work: addedBlock(ownerReportWorkSchema),
  /** Null when the read failed. */
  setupFacts: addedBlock(ownerReportSetupFactsSchema),
  /** The marketing team's plan for the next month; null when none was written. */
  nextMonthPlan: z.string().nullable(),
  /** The team's note, their ask of the owner and their typed numbers; null when they wrote none of these. */
  teamInput: addedBlock(ownerReportTeamInputSchema),
  footnote: z.string(),
  preparedBy: z.string(),
});
export type OwnerReport = z.infer<typeof ownerReportSchema>;

/** The page payload: the salon, the months it can show and the month's report (public page and staff preview alike). */
export const ownerReportPageSchema = z.object({
  salon: z.object({
    name: z.string(),
    logoUrl: z.string().nullable(),
    brandColor: z.string().nullable(),
    /** The address label the landing app matches against the request host; null without a web address. */
    subdomain: z.string().nullable(),
    canonicalOrigin: z.string().nullable(),
  }),
  /** Newest first; the current month first and flagged `partial`. */
  months: z.array(ownerReportMonthOptionSchema),
  report: ownerReportSchema,
});
export type OwnerReportPage = z.infer<typeof ownerReportPageSchema>;
